import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Grid,
  Divider,
  Breadcrumbs,
  Typography,
  Link,
} from '@material-ui/core';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import EditOutlined from '@material-ui/icons/EditOutlined';
import FilterListIcon from '@material-ui/icons/FilterList';
import {
  FilterChip,
  Spinner,
  QuickSearch,
  useAPI,
  VerticalBoxContainer,
  PageContentWrapper,
} from '@shared/components';
import PageHeader from '../../components/PageHeader';
import ExcelExportButton from '../../components/ExcelExportButton';
import { handleChipDelete } from '../../utils/filterHelpers';
import useTheme from '../../hooks/useTheme';
import axios from '../../utils/axios';
import useGlobalAlertContext from '../../hooks/useGlobalAlertContext';
import AttributeModal from '../Attributes/Modal';
import Table from './Table';
import Filter from './Filter';

const fields = [
  {
    label: 'Tip dokumenta',
    value: 'documentTypeName',
  },
  {
    label: 'Redni broj',
    value: 'sequenceNumber',
  },
  {
    label: 'Inicijalna vrijednost',
    value: 'initialValue',
  },
  {
    label: 'SQL upit za podatke',
    value: 'sqlDataCode',
  },
  {
    label: 'Uputstvo (hint)',
    value: 'instructions',
  },
  {
    label: 'Obavezan',
    value: 'requiredField',
  },
  {
    label: 'Spasi',
    value: 'saveField',
  },
  {
    label: 'Važi',
    value: 'valid',
  },
];

const getLabelText = (field) => {
  const map = {
    documentTypeName: 'Tip dokumenta',
    sequenceNumber: 'Redni broj',
    initialValue: 'Inicijalna vrijednost',
    sqlDataCode: 'SQL upit za podatke',
    instructions: 'Uputstvo',
    requiredField: 'Obavezan',
    saveField: 'Spasi',
    valid: 'Važi',
  };
  return map[field];
};

const getChipValue = (chip, filters) => {
  let value;
  if (chip === 'requiredField' || chip === 'valid') value = filters[chip] === 'D' ? 'DA' : 'NE';
  else if (chip === 'saveField') value = filters[chip] === 2 ? 'DA' : 'NE';
  else value = filters[chip];
  return value;
};

const AttributeDocumentTypes = () => {
  const theme = useTheme();
  const [attribute, setAttribute] = useState({});
  const [filters, setFilters] = useState({});
  const [sorters, setSorters] = useState([]);
  const history = useHistory();
  const { id } = useParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [triggerRerender, setRerender] = useState(false);

  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [attributeModalVisible, setAttributeModalVisible] = useState(false);
  const { sendSuccessAlertData, sendErrorAlertData } = useGlobalAlertContext();

  const [, , attributeLoading] = useAPI(
    () =>
      axios
        .get(`attributes/${id}`)
        .then(({ data }) => {
          setAttribute(data);
        })
        .catch((error) => {
          sendErrorAlertData(
            `${
              error?.response?.data?.details?.reason ||
              error?.response?.data?.error?.message ||
              `Došlo je do greške prilikom preuzimanja informacija o atributu`
            }`,
          );
          history.replace('/attributes');
        }),
    [id, triggerRerender],
  );

  const [attributeDocumentTypes = { nodes: [], totalCount: 0 }, error, loading] = useAPI(
    () =>
      axios
        .get(`attributes-document-types/group/${id}`, {
          params: {
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            conditions: {
              ...filters,
              saveField: filters.saveField !== undefined ? filters.saveField - 1 : undefined,
            },
            orderBy: [...sorters, '"sequenceNumber" asc'],
          },
        })
        .then(({ data }) => data),
    [page, rowsPerPage, filters, sorters, triggerRerender],
  );

  const handleFilterChange = (filterValues) => {
    setPage(0);
    setFilters({ ...filterValues });
  };

  return (
    <VerticalBoxContainer>
      <Filter
        currentValues={filters}
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        handleFilterChange={handleFilterChange}
        getLabelText={getLabelText}
        getChipValue={getChipValue}
        setFilters={setFilters}
        setPage={setPage}
      />
      <AttributeModal
        attribute={attribute}
        setAttribute={setAttribute}
        resetAttribute={() => setAttribute(undefined)}
        visible={attributeModalVisible}
        setVisible={setAttributeModalVisible}
        setRerender={setRerender}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        detailed
      />
      <PageHeader
        title="Tipovi dokumenata koji imaju dati atribut"
        showBackIcon
        extra={
          <Button
            variant="contained"
            color="primary"
            startIcon={attributeLoading ? <Spinner size={15} /> : <EditOutlined />}
            disabled={attributeLoading}
            onClick={() => setAttributeModalVisible(true)}
          >
            <Box fontSize="small">Uredi atribut</Box>
          </Button>
        }
      />
      <PageContentWrapper>
        <Breadcrumbs style={{ paddingBottom: `${theme.spacing(5)}px` }}>
          <Link variant="body1" color="inherit" to="/" component={RouterLink}>
            Početna
          </Link>
          <Typography variant="body1" color="textPrimary">
            Šifarnici
          </Typography>
          <Link variant="body1" color="inherit" to="/attributes" component={RouterLink}>
            Atributi
          </Link>
          <Typography>Tipovi dokumenata koji imaju dati atribut</Typography>
        </Breadcrumbs>
        <Card>
          <CardHeader
            title={
              <QuickSearch
                name="documentTypeName"
                placeholder="Brza pretraga po tipu dokumenta"
                setFilters={setFilters}
                mainFilter={filters}
                setPage={setPage}
              />
            }
            action={
              <Grid container spacing={4}>
                <Grid item>
                  <Button
                    color="primary"
                    disabled={Object.keys(filters).filter((k) => filters[k])?.length === 0}
                    startIcon={<CloseIcon fontSize="small" />}
                    onClick={() => setFilters({})}
                  >
                    <Box fontSize="small">Ukloni sve filtere</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon fontSize="small" />}
                    onClick={() => setFilterModalVisible(true)}
                  >
                    <Box fontSize="small">Filteri</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <ExcelExportButton
                    totalCount={attributeDocumentTypes.totalCount}
                    fields={fields}
                    sorters={[...sorters, '"sequenceNumber" asc']}
                    filters={{
                      ...filters,
                      saveField:
                        filters.saveField !== undefined ? filters.saveField - 1 : undefined,
                    }}
                    variant="contained"
                    routeName={`attributes-document-types/group/${id}`}
                    fileName="tipovi-dokumenata-atribut"
                  />
                </Grid>
              </Grid>
            }
          />
          <Divider />
          {Object.keys(filters).filter((k) => filters[k])?.length !== 0 && (
            <>
              <Box ml={2} p={2} display="flex" alignItems="center" flexWrap="wrap">
                {Object.keys(filters).map(
                  (chip) =>
                    filters[chip] !== undefined && (
                      <FilterChip
                        color="primary"
                        variant="outlined"
                        key={chip}
                        label={`${getLabelText(chip)}: ${getChipValue(chip, filters)}`}
                        onDelete={() => handleChipDelete(chip, setFilters, setPage)}
                      />
                    ),
                )}
              </Box>
            </>
          )}
          <Table
            attributeDocumentTypes={attributeDocumentTypes.nodes}
            page={page}
            setPage={setPage}
            totalCount={attributeDocumentTypes.totalCount}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            error={error}
            sorters={sorters}
            setSorters={setSorters}
            loading={loading}
          />
        </Card>
      </PageContentWrapper>
    </VerticalBoxContainer>
  );
};

export default AttributeDocumentTypes;
